import './search.scss'
import { BaseButton, ResidenceCardGrid, ResidenceCardSkeletonGrid, SearchForm } from 'components'
import { usePageTitle, useSearch, useSearchQueryString } from 'hooks'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { IonButton, IonSpinner } from '@ionic/react'
import { ISearchParams, ISearchResult } from 'interfaces'
import { residenceService, routeService, stringService, transformerService } from 'services'
import BasePage from 'pages/base-page/base-page.page'
import qs from 'qs'
import { useHistory } from 'react-router'
const SEARCH_PAGE_SIZE = parseInt(process.env.REACT_APP_SEARCH_PAGE_SIZE ?? '10')

const DEFAULT_SEARCH_PARAMS: ISearchParams = {
  ...residenceService.getDefaultSearchParams(),
  limit: SEARCH_PAGE_SIZE,
  offset: 0,
}

const Search: React.FC = () => {
  const [resultsLoaded, setResultsLoaded] = useState(false)
  const [loadMore, setLoadMore] = useState<boolean>(false)
  const history = useHistory()
  const { searchParams: searchQueryParams } = useSearchQueryString()
  const [searchParams, setSearchParams] = useState<ISearchParams | undefined>()
  const [modifySearch, setModifySearch] = useState(false)
  const defaultResult: ISearchResult = useMemo((): ISearchResult => {
    return { hasMore: false, data: [] }
  }, [])
  const searchResults = useSearch(searchParams, defaultResult)

  const { t } = useTranslation()

  const titlePage = stringService.capitalizeFirstLetter(t('search'))
  usePageTitle(titlePage!)

  useEffect(() => {
    if (searchQueryParams) {
      setSearchParams({ ...DEFAULT_SEARCH_PARAMS, ...searchQueryParams })
    }
  }, [searchQueryParams])

  useEffect(() => {
    if (searchResults) {
      setResultsLoaded(true)
      setLoadMore(false)
    }
  }, [searchResults])

  useEffect(() => {
    setResultsLoaded(false)
  }, [searchParams])

  const onSubmitSearch = useCallback((params: ISearchParams) => {
    const query_obj = transformerService.toISearchQueryString(
      params.profileTypeId,
      params.cityId,
      params.dateFrom,
      params.dateTo,
      params.eventCode,
      params.guestsPerRoom,
    )
    setResultsLoaded(false)
    setSearchParams({
      ...params,
      limit: SEARCH_PAGE_SIZE,
      offset: 0,
    })
    setModifySearch(false)
    history.push(routeService.generateRoutePath('/search') + '?' + qs.stringify(query_obj))
  }, [])

  const onLoadMoreClick = () => {
    setLoadMore(true)
    setSearchParams((sp) => {
      return {
        ...sp,
        limit: SEARCH_PAGE_SIZE,
        offset: (sp?.offset || 0) + SEARCH_PAGE_SIZE,
      }
    })
  }

  return (
    <BasePage pageClass="edisu-page-search">
      <div className="search-wrapper">
        <IonButton
          className={`buttonSearch modifySearch${modifySearch ? 'True' : ''}`}
          onClick={() => {
            if (modifySearch) {
              setModifySearch(false)
            } else {
              setModifySearch(true)
            }
          }}
        >
          {t('change_search')}
        </IonButton>
        <div className={`search-cnt${modifySearch ? '' : 'False'}`}>
          <SearchForm defaultParams={searchParams} onSubmitFunction={onSubmitSearch} />
        </div>
      </div>

      <div className="result-cnt mobile-spaced ">
        <ResidenceCardGrid
          openLinkInNewPage={true}
          residences={searchResults?.data?.map((residence) => {
            return { residence }
          })}
          searchParams={searchParams}
          loadingComponent={!resultsLoaded && <ResidenceCardSkeletonGrid count={4} />}
        />
        {resultsLoaded && searchResults?.hasMore && (
          <div className="load-more-cnt">
            {loadMore ? (
              <IonSpinner name="crescent" color="primary" />
            ) : (
              <BaseButton onClickFunction={() => onLoadMoreClick()} label={t('load_more')} />
            )}
          </div>
        )}
      </div>
    </BasePage>
  )
}

export default Search
